import React, { useEffect, useRef, useState } from 'react';
import {
    GatsbyImage,
    getImage,
    IGatsbyImageData,
    withArtDirection,
} from 'gatsby-plugin-image';
import { SmartLink } from '../SmartLink/SmartLink';
import { useDebounce } from '../../shared/useDebounce';

export interface CarouselSlideDataProps {
    eyebrow: string;
    title: string;
    text: string;
    ctas: {
        title: string;
        uri: string;
    }[];
    image: IGatsbyImageData;
    mobileImage: IGatsbyImageData;
    imageAlt: string;
}

interface carouselProps {
    slide: CarouselSlideDataProps;
    activeSlide: boolean;
    direction?: string;
    slideNo?: number;
    prevSlide?: boolean;
    nextSlide?: boolean;
    setCarouselHeight: (number: number) => void;
}

export const HeroCarouselSlide = ({
    slide,
    activeSlide,
    direction,
    prevSlide,
    nextSlide,
    setCarouselHeight,
    slideNo,
}: carouselProps) => {
    if (!slide.image) {
        // eslint-disable-next-line
        console.error(
            `Images are missing from slide - please author 1 image per slide!`
        );
        return <p>Missing image in slide</p>;
    }
    const responsiveImages = withArtDirection(getImage(slide.image), [
        {
            media: `(max-width: 767px)`,
            image: getImage(
                slide.mobileImage ? slide.mobileImage : slide.image
            ),
        },
    ]);
    const slideRef = useRef(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(1);

    useEffect(() => {
        const resizeSlide = () => {
            if (activeSlide && slideRef.current) {
                setCurrentSlideIndex(slideNo);
                setCarouselHeight(slideRef.current.clientHeight);
            }
        };

        resizeSlide();
        window.addEventListener(`resize`, () => {
            resizeSlide();
        });

        return () => {
            window.removeEventListener(`resize`, () => {
                resizeSlide();
            });
        };
    }, [setCarouselHeight, activeSlide]);

    return (
        <div
            className={`slide-container ${direction}
            ${activeSlide ? `active` : ``}
            ${!prevSlide ? `` : `previous`}
            ${nextSlide ? `next` : ``}
            content`}
            ref={slideRef}
        >
            <div className="image-content">
                <GatsbyImage image={responsiveImages} alt={slide.imageAlt} />
                <div className="slide-titles">
                    <div className="titles-wrapper">
                        <p className="typestack-eyebrow">{slide.eyebrow}</p>
                        <h1 className="typestack-title-1">{slide.title}</h1>
                    </div>
                </div>
            </div>

            <div
                className="slide-content"
                dangerouslySetInnerHTML={{
                    __html: slide.text,
                }}
            ></div>

            {slide.ctas.length > 0 && (
                <div className="slide-ctas">
                    {slide.ctas?.map((cta, idx) => (
                        <SmartLink
                            key={`${cta}_${idx}`}
                            url={cta.uri}
                            classNames={[`typestack-body-1`]}
                        >
                            {cta.title}
                        </SmartLink>
                    ))}
                </div>
            )}
        </div>
    );
};
